import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Input, Space, Tag, Typography } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, ShieldCheck } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authApi } from '@api/auth';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useAuthStore } from '@features/auth/store';

const { Text, Paragraph } = Typography;

type Mode = 'idle' | 'enrolling' | 'enrolled';

/**
 * Self-service 2FA yönetimi. K-13 #21 ile `UserProfileDto.twoFactorEnabled`
 * BE'den gelir — mevcut durum kart başlığında rozetle gösterilir ve
 * etkinleştirme/devre dışı bırakma sonrası `me` ile tazelenir. Kart hem "Kur"
 * hem "Devre dışı bırak" akışlarını sunar, geçerliliği BE zorlar. Setup
 * secret'ı client-side kalır (QR lokal üretilir — harici servis YOK); yedek
 * kodlar bir kez gösterilir.
 */
export function TwoFactorCard() {
  const { t } = useTranslation(['profile', 'common']);
  const { user } = useAuth();

  // K-13 #21 — 2FA durumunu me endpoint'inden tazele (rozet + eski oturumlar).
  const refreshProfile = () => {
    void authApi
      .me()
      .then((fresh) => useAuthStore.getState().setUser(fresh))
      .catch(() => undefined);
  };
  const [mode, setMode] = useState<Mode>('idle');
  const [setup, setSetup] = useState<{ secret: string; provisioningUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showDisable, setShowDisable] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  const copy = (value: string, label: string) => {
    void navigator.clipboard
      .writeText(value)
      .then(() => toast.success(t('twoFactor.copied', { label })))
      .catch(() => toast.error(t('twoFactor.copyFailed')));
  };

  const setupMutation = useMutation({
    mutationFn: () => authApi.setupTwoFactor(),
    onSuccess: (data) => {
      setSetup(data);
      setMode('enrolling');
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const verifyMutation = useMutation({
    mutationFn: (c: string) => authApi.verifyTwoFactor(c),
    onSuccess: (r) => {
      setBackupCodes(r.backupCodes);
      setCode('');
      setMode('enrolled');
      toast.success(t('twoFactor.enabledToast'));
      refreshProfile();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const disableMutation = useMutation({
    mutationFn: (c: string) => authApi.disableTwoFactor(c),
    onSuccess: () => {
      toast.success(t('twoFactor.disabledToast'));
      refreshProfile();
      setShowDisable(false);
      setDisableCode('');
      setSetup(null);
      setBackupCodes([]);
      setMode('idle');
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  return (
    <Card
      title={
        <Space>
          <ShieldCheck size={18} />
          {t('twoFactor.title')}
          {user?.twoFactorEnabled === true ? (
            <Tag color="green">{t('twoFactor.statusEnabled')}</Tag>
          ) : user?.twoFactorEnabled === false ? (
            <Tag>{t('twoFactor.statusDisabled')}</Tag>
          ) : null}
        </Space>
      }
    >
      <Paragraph type="secondary">{t('twoFactor.description')}</Paragraph>

      {mode === 'idle' ? (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Button
            type="primary"
            loading={setupMutation.isPending}
            onClick={() => setupMutation.mutate()}
          >
            {t('twoFactor.setupButton')}
          </Button>

          {showDisable ? (
            <Card size="small" title={t('twoFactor.disableTitle')}>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text type="secondary">{t('twoFactor.disableHint')}</Text>
                <Input
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  placeholder={t('twoFactor.codePlaceholder')}
                  inputMode="numeric"
                  maxLength={20}
                />
                <Space>
                  <Button
                    danger
                    loading={disableMutation.isPending}
                    disabled={disableCode.trim().length === 0}
                    onClick={() => disableMutation.mutate(disableCode.trim())}
                  >
                    {t('twoFactor.disableConfirm')}
                  </Button>
                  <Button onClick={() => setShowDisable(false)}>{t('common:actions.cancel')}</Button>
                </Space>
              </Space>
            </Card>
          ) : (
            <Button type="link" style={{ paddingInline: 0 }} onClick={() => setShowDisable(true)}>
              {t('twoFactor.disableButton')}
            </Button>
          )}
        </Space>
      ) : null}

      {mode === 'enrolling' && setup ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Text>{t('twoFactor.scanHint')}</Text>
          <div style={{ background: '#fff', padding: 12, width: 'fit-content', borderRadius: 8 }}>
            <QRCodeSVG value={setup.provisioningUri} size={180} />
          </div>
          <div>
            <Text type="secondary">{t('twoFactor.manualHint')}</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <Text code copyable={false} style={{ fontSize: 14 }}>
                {setup.secret}
              </Text>
              <Button
                size="small"
                icon={<Copy size={13} />}
                onClick={() => copy(setup.secret, t('twoFactor.secretLabel'))}
              >
                {t('twoFactor.copy')}
              </Button>
            </div>
          </div>
          <div>
            <Text>{t('twoFactor.enterCode')}</Text>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('twoFactor.codePlaceholder')}
              inputMode="numeric"
              maxLength={20}
              style={{ marginTop: 6 }}
              onPressEnter={() => {
                if (code.trim()) verifyMutation.mutate(code.trim());
              }}
            />
          </div>
          <Space>
            <Button
              type="primary"
              loading={verifyMutation.isPending}
              disabled={code.trim().length === 0}
              onClick={() => verifyMutation.mutate(code.trim())}
            >
              {t('twoFactor.verifyButton')}
            </Button>
            <Button
              onClick={() => {
                setMode('idle');
                setSetup(null);
                setCode('');
              }}
            >
              {t('common:actions.cancel')}
            </Button>
          </Space>
        </Space>
      ) : null}

      {mode === 'enrolled' ? (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert type="success" showIcon message={t('twoFactor.enabledTitle')} />
          <Alert
            type="warning"
            showIcon
            message={t('twoFactor.backupTitle')}
            description={t('twoFactor.backupWarning')}
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 8,
            }}
          >
            {backupCodes.map((c) => (
              <Tag key={c} style={{ fontFamily: 'monospace', fontSize: 13, textAlign: 'center' }}>
                {c}
              </Tag>
            ))}
          </div>
          <Button
            icon={<Copy size={14} />}
            onClick={() => copy(backupCodes.join('\n'), t('twoFactor.backupLabel'))}
          >
            {t('twoFactor.copyBackup')}
          </Button>
          <Button type="link" style={{ paddingInline: 0 }} onClick={() => setMode('idle')}>
            {t('twoFactor.done')}
          </Button>
        </Space>
      ) : null}
    </Card>
  );
}
